import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadProductImage, deleteProductImage, updateProductImages } from '../services/productService';

/**
 * Hook for uploading a product image
 * @param {string} productId - ID of the product
 * @returns {import('@tanstack/react-query').UseMutationResult<any, Error, File>}
 */
export function useUploadProductImage(productId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file) => uploadProductImage(productId, file),
    onSuccess: (data) => {
      queryClient.setQueryData(['product', productId], (oldData) => ({
        ...oldData,
        images: [...oldData.images, {
          id: `temp-${Date.now()}`,
          url: data.url,
          isPrimary: false,
          sortOrder: oldData.images.length
        }]
      }));
    },
  });
}

/**
 * Hook for deleting a product image
 * @param {string} productId - ID of the product
 * @returns {import('@tanstack/react-query').UseMutationResult<any, Error, string>}
 */
export function useDeleteProductImage(productId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (imageId) => deleteProductImage(productId, imageId),
    onSuccess: (_, imageId) => {
      queryClient.setQueryData(['product', productId], (oldData) => ({
        ...oldData,
        images: oldData.images.filter((img) => img.id !== imageId)
      }));
    },
  });
}

/**
 * Hook for updating product images
 * @param {string} productId - ID of the product
 * @returns {import('@tanstack/react-query').UseMutationResult<any, Error, Array<{id: string, sortOrder: number, isPrimary: boolean}>>}
 */
export function useUpdateProductImages(productId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (images) => updateProductImages(productId, images),
    onSuccess: (_, images) => {
      queryClient.setQueryData(['product', productId], (oldData) => ({
        ...oldData,
        images: oldData.images.map((img) => {
          const updatedImage = images.find(i => i.id === img.id);
          if (updatedImage) {
            return {
              ...img,
              sortOrder: updatedImage.sortOrder,
              isPrimary: updatedImage.isPrimary
            };
          }
          return img;
        })
      }));
    },
  });
} 